import posthog from 'posthog-js'

// Sensitive query params (tokens, keys, ...) are stripped from captured URLs
let deniedParams = null

// Every property whose key ends in "url" or "referrer" gets scrubbed
const URL_PROPERTY = /(?:url|referrer)$/i

// Substring match, mirroring ActiveSupport::ParameterFilter: "token" covers reset_password_token,
// confirmation_token and auth_token; "_key" covers secret_key and postback_key.
function isDenied(name) {
    if (!deniedParams) return false

    const key = name.toLowerCase()
    return deniedParams.some((denied) => key.includes(denied))
}

// Fail closed on the query string specifically: with no list, drop every param rather than ship a
// password-reset token because the bootstrap arrived without one. (Properties can't fail closed
// the same way — deleting all of them would leave nothing to analyze — so posthog's own
// exact-key property_denylist stays as the backstop there.)
function isDeniedParam(param) {
    return deniedParams ? isDenied(param) : true
}

function scrubUrl(raw) {
    try {
        const u = new URL(raw)
        ;[...u.searchParams.keys()].forEach((k) => {
            if (isDeniedParam(k)) u.searchParams.delete(k)
        })
        return u.toString()
    } catch {
        return raw
    }
}

// Drops denied properties outright, then strips denied params from whatever URLs remain.
// posthog's property_denylist can't do the former on its own: it deletes exact keys only, so it
// can't express the substring rules Rails' list is written in.
// Non-URL values ("$direct" for a missing referrer) fail the URL parse and pass through as-is.
function scrubProperties(props) {
    if (!props) return
    for (const [key, value] of Object.entries(props)) {
        if (isDenied(key)) delete props[key]
        else if (typeof value === 'string' && URL_PROPERTY.test(key)) props[key] = scrubUrl(value)
    }
}

export function scrub(event) {
    if (!event) return event
    const p = event.properties || {}
    scrubProperties(p)
    scrubProperties(p.$set)
    scrubProperties(p.$set_once)
    return event
}

let started = false

// Called on every turbo:load. posthog.init() runs once, but identity must reconcile on
// every visit
export function initAnalytics() {
    const cfg = window.posthogBootstrap
    if (!cfg) return

    if (!started) {
        started = true
        initPosthog(cfg)
    }

    // Post-logout landing page: clear any prior user's identity (shared machines).
    if (cfg.reset) posthog.reset()

    if (cfg.distinct_id) {
        // identify() merges the prior anonymous (signup/auth) session into this person —
        // the client is the single writer of person props. Skipped when already identified
        // as this user, so routine navigation doesn't re-send $set on every page.
        if (posthog.get_distinct_id() !== cfg.distinct_id) {
            posthog.identify(cfg.distinct_id, cfg.person, cfg.person_once /* $set_once */)
        }

        // Super properties on every event while identified — user_id / company_id /
        // company_name / is_employee, mobile's names verbatim (AnalyticsIdentity.tsx), so any
        // event filters by user/company with no joins. Registered every visit (idempotent,
        // persisted by posthog-js) so a company switch updates them; reset() clears them.
        if (cfg.identified_super_properties) posthog.register(cfg.identified_super_properties)

        // Re-group on company switch (a Turbo redirect, so it lands here, not in init).
        if (cfg.group && posthog.getGroups()[cfg.group.type] !== cfg.group.key) {
            posthog.group(cfg.group.type, cfg.group.key, cfg.group.properties)
            posthog.setPersonProperties(cfg.person) // person props carry company_id/name too
        }
    }
}

function initPosthog(cfg) {
    deniedParams = cfg.denied_params || null // must precede init(): before_send fires from it

    posthog.init(cfg.key, {
        api_host: cfg.host,
        person_profiles: 'identified_only', // anonymous funnel still captured; profile created on identify
        // Library-native SPA pageviews: posthog-js listens to the History API (pushState),
        // which Turbo Drive uses — so no manual turbo:load pageview handler is needed.
        capture_pageview: 'history_change',
        capture_pageleave: true,
        autocapture: true,
        // ---- PII: prefer built-in masking over custom code ----
        // Narrower than the name suggests: it masks the values of known ad-click IDs (gclid,
        // fbclid, ...) inside captured URLs
        mask_personal_data_properties: true,
        // exact-key strip on EVERY event
        property_denylist: [
            'card_number',
            'cvv',
            'api_key',
            'authentication_token',
            'secret_key',
            'key',
            'postback_key',
        ],
        session_recording: { maskAllInputs: true },
        disable_session_recording: true, // off for now: volume + PII; revisit deliberately
        before_send: scrub, // app-specific bit: strip sensitive query params from captured URLs
    })

    // app_env + product on every event so prod dashboards can exclude staging/internal.
    if (cfg.super_properties) posthog.register(cfg.super_properties)
}

// Capture a curated product event with a fixed `category:object_action` name.
// No-ops until PostHog is initialized (anonymous/disabled/impersonation), so callers
// don't need to guard.
export function track(name, props = {}) {
    if (!started || !name) return
    posthog.capture(name, props)
}
